import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

const Snackbar = () => {
  const snackbarState = useSelector((state) => state.Buzzcom.snackbarState);
  const { message, onRetry, show, onClose, status } = snackbarState;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (show) {
      setVisible(true);
      const timer = setTimeout(() => {
        setVisible(false);
        if (onClose) onClose();
      }, 4000); // Auto-hide after 4 seconds

      return () => clearTimeout(timer); // Cleanup timer on unmount
    } else {
      setVisible(false);
    }
  }, [snackbarState]);

  // Function to get styles based on the snackbar status
  const getSnackbarStyles = (status) => {
    switch (status) {
      case "success":
        return {
          backgroundColor: "#28a745", // Green background for success
          color: "white",
          border: "2px solid #ffffff",
        };
      case "info":
        return {
          backgroundColor: "#17a2b8", // Blue background for info
          color: "white",
          border: "2px solid #ffffff",
        };
      case "error":
        return {
          backgroundColor: "#dc3545", // Red background for error
          color: "white",
          border: "2px solid #ffffff",
        };
      default:
        return {
          backgroundColor: "#ff6500", // Default background (fallback)
          color: "#000",
          border: "1px solid #ffffff",
        };
    }
  };

  // Function to get the toast header text based on the status
  const getHeaderText = (status) => {
    switch (status) {
      case "success":
        return "Success";
      case "info":
        return "Information";
      case "error":
        return "Error";
      default:
        return "Notification";
    }
  };

  // Render only if `visible` is true
  if (!visible) {
    return null;
  }

  return (
    <div
      className="toast show position-fixed top-0 end-0 m-3"
      style={{
        zIndex: 9999,
        ...getSnackbarStyles(status),
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
      }}
    >
      <div
        className="toast-header"
        style={{ backgroundColor: getSnackbarStyles(status).backgroundColor }}
      >
        <strong className="me-auto">{getHeaderText(status)}</strong>
        <button
          type="button"
          className="btn-close"
          aria-label="Close"
          onClick={() => {
            setVisible(false);
            if (onClose) onClose();
          }}
        ></button>
      </div>
      <div className="toast-body">
        <div>{message}</div>
        {status === "error" && onRetry && (
          <button
            className="btn btn-outline-danger btn-sm mt-2"
            onClick={() => {
              if (onRetry) onRetry();
              setVisible(false); // Hide snackbar after retry
            }}
          >
            Retry
          </button>
        )}
      </div>
    </div>
  );
};

Snackbar.propTypes = {
  message: PropTypes.string, // The error message to display
  onRetry: PropTypes.func, // Function to call on retry (only for errors)
  show: PropTypes.bool, // Controls the visibility of the snackbar
  onClose: PropTypes.func, // Optional callback for when the snackbar is closed
  status: PropTypes.oneOf(["success", "info", "error"]), // Determines the snackbar's type
};

export default Snackbar;
